import {
  Expressions,
  Variable,
  matchExpression,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {concat, constant, emptyArray} from '@packages/utilities/fp/utils';
import {DYNAMO_VARIABLE} from './constants';

import type {Expression} from '@packages/systems/wfdl-ast';

const isDynamoVariable = Variable.equals(DYNAMO_VARIABLE);

type BindingPaths = ReadonlyArray<string[]>;

const getDynamoBindingPathFromSelect = (
  from: Expression,
  prop: string
): string[] | null => {
  const fromVariable = Expressions.getVariable(from);

  // ignore selects that do not target Dynamo env
  if (fromVariable === null || !isDynamoVariable(fromVariable)) {
    return null;
  }

  return prop.split('.');
};

const getDynamoBindingPathFromExpression = (
  expr: Expression
): string[] | null => {
  const select = Expressions.getSelect(expr);
  if (!select) {
    return null;
  }

  return getDynamoBindingPathFromSelect(select.from, select.prop);
};

export const getDynamoBindingPaths: (arg1: Expression) => BindingPaths =
  Expressions.everything(concat)(
    matchExpression({
      Select: ({from, prop}) => {
        const bindingPath = getDynamoBindingPathFromSelect(from, prop);
        if (bindingPath === null) {
          return Expressions.resume(emptyArray);
        }

        return Expressions.stop([bindingPath]);
      },
      Element: constant(Expressions.stop(emptyArray)),
      _: constant(Expressions.resume(emptyArray)),
    })
  );

// Companion to getDynamoBindingPaths that also tells us _where_ those bindings live.
export const getDynamoBindingPathsWithExprPath = (
  expr: Expression
): Map<string[], ExpressionPath[]> => {
  const bindingPathsByKey = new Map<
    string,
    {bindingPath: string[]; exprPaths: ExpressionPath[]}
  >();

  const matchingExprPaths = Expressions.findPathsWithStoppable(
    expr,
    matchExpression({
      Element: constant(Expressions.stop(false)),
      Call: ({arg}) =>
        getDynamoBindingPathFromExpression(arg)
          ? Expressions.stop(true)
          : Expressions.resume(false),
      Select: ({from, prop}) =>
        getDynamoBindingPathFromSelect(from, prop)
          ? Expressions.stop(true)
          : Expressions.resume(false),
      _: constant(Expressions.resume(false)),
    })
  );

  matchingExprPaths.forEach((exprPath) => {
    const exprAtPath = Expressions.getIn(expr, exprPath);
    if (!exprAtPath) {
      return;
    }

    const call = Expressions.getCall(exprAtPath);
    const bindingPath = getDynamoBindingPathFromExpression(
      call ? call.arg : exprAtPath
    );
    if (bindingPath === null) {
      return;
    }

    const key = bindingPath.join('.');
    const bindingPathEntry = bindingPathsByKey.get(key);
    if (bindingPathEntry) {
      bindingPathEntry.exprPaths.push(exprPath);
      return;
    }

    bindingPathsByKey.set(key, {bindingPath, exprPaths: [exprPath]});
  });

  const bindingPathsWithExprPaths = new Map<string[], ExpressionPath[]>();
  bindingPathsByKey.forEach(({bindingPath, exprPaths}) => {
    bindingPathsWithExprPaths.set(bindingPath, exprPaths);
  });
  return bindingPathsWithExprPaths;
};
